import { consume } from "@lit/context";
import {
    type EntityFilter,
    type GrantingProfile,
    PERMISSIONS,
    type Permission,
    type PermissionInfo,
    Role,
    type WorkspaceFilter,
    canGrantPermission,
    extractWorkspaceFilters,
    togglePermissions,
} from "@pentacode/core";
import { activeIcon } from "@pentacode/ui/components";
import { LitElement, type PropertyValues, type TemplateResult, css, html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { type EnabledFeatures, enabledFeatures } from "../../../contexts/features-context";
import { popover } from "../../../directives/popover";
import { confirm } from "../../../elements/alert-dialog";
import { Checkbox, type CheckboxButton } from "../../../elements/checkbox";
import "../../../elements/entity-filters";
import type { EntityFiltersEl } from "../../../elements/entity-filters";
import "../../../elements/popover";
import "../../../elements/scroller";
import { app } from "../../../init";
import { colors, shared } from "../../../styles";

@customElement("ptc-permissions-form")
export class PermissionsForm extends LitElement {
    @property({ type: Array })
    permissions: Permission[];

    @property({ attribute: false })
    filters: EntityFilter[];

    @property({ type: Number })
    accessRole: Role;

    @property({ type: Boolean })
    isNew = false;

    @property({ type: Boolean })
    readonly = false;

    @property({ attribute: false })
    grantingProfile: GrantingProfile;

    @consume({ context: enabledFeatures, subscribe: true })
    private _enabledFeatures?: EnabledFeatures;

    @query("form")
    private _form: HTMLFormElement;

    @query("ptc-entity-filters[name='filters']")
    private _filtersInput: EntityFiltersEl;

    @query("select[name='role']")
    private _roleSelect: HTMLSelectElement;

    @state()
    private _page: "manage" | "staff" = "staff";

    get data(): { permissions: Permission[]; role: Role; filters: WorkspaceFilter[] } {
        if (!this._form) {
            return { permissions: [], role: Role.Worker, filters: [] };
        }
        const formData = new FormData(this._form);

        let role = formData.get("role");

        // get the role from the DOM manually, as form data does not include disabled values
        if (!role) {
            role = this._roleSelect.selectedOptions[0]?.value;
        }

        return {
            permissions: [...new Set<Permission>(formData.getAll("permissions") as Permission[])],
            role: Number(role ?? Role.Worker) as Role,
            filters: extractWorkspaceFilters(this._filtersInput.filters),
        };
    }

    get hasChanged() {
        if (this.readonly) return false;

        const { permissions, role, filters } = this.data;
        return (
            role !== this.accessRole ||
            permissions.length !== this.permissions.length ||
            permissions.some((p) => !this.permissions.includes(p as Permission)) ||
            filters.length !== this.filters.length ||
            filters.some((filter) => !this.filters.some((f) => f.type === filter.type && f.value === filter.value))
        );
    }

    protected firstUpdated(): void {
        this.requestUpdate("permissions");
    }

    updated(changes: PropertyValues) {
        let hasRelevantChanges = false;
        if (changes.has("accessRole")) {
            this._roleSelect.value = this.accessRole.toString();
            this._page = this.accessRole > Role.Manager ? "staff" : "manage";
            hasRelevantChanges = true;
        }

        if (changes.has("permissions")) {
            const permsEls = this.renderRoot.querySelectorAll(
                `ptc-checkbox-button[name="permissions"]`
            ) as NodeListOf<Checkbox>;
            for (const el of permsEls) {
                const shouldBeChecked = this.accessRole <= Role.Owner || this.permissions.some((p) => p === el.value);

                if (shouldBeChecked !== el.checked) {
                    el.checked = shouldBeChecked;
                    hasRelevantChanges = true;
                }
            }
        }

        if (changes.has("filters")) {
            this._filtersInput.filters = this.accessRole === Role.Owner ? [] : this.filters;
            hasRelevantChanges = true;
        }

        if (hasRelevantChanges) {
            this.requestUpdate();
        }
    }

    private _permissionChanged(e: Event) {
        const input = e.target as Checkbox;
        const key = input.value as Permission;
        const checked = input.checked;

        // Store old permissions for comparison
        const oldPermissions = [...this.data.permissions];

        // Get related permissions for this key
        const relatedPermissions = app.getRelatedPermissions(key);

        // Use togglePermissions to handle sub-permissions correctly
        const newPermissions = togglePermissions({
            currentPermissions: this.data.permissions,
            checked,
            grantingProfile: this.grantingProfile,
            relatedPermissions,
            asAdmin: app.account?.admin,
        });

        // Update the data object
        this.data.permissions = newPermissions;

        // Update checkboxes to reflect the changes
        this._updateCheckboxes(oldPermissions, newPermissions);

        this.requestUpdate();
        this.dispatchEvent(new CustomEvent("change"));
    }

    private _updateCheckboxes(oldPermissions: Permission[], newPermissions: Permission[]) {
        // Update checkboxes for new permissions
        for (const permission of newPermissions) {
            const checkBoxes = this.renderRoot.querySelectorAll(
                `ptc-checkbox-button[name="permissions"][value="${permission}"]`
            ) as NodeListOf<CheckboxButton>;
            [...checkBoxes].forEach((el) => (el.checked = true));
        }

        // Update checkboxes for removed permissions
        const removedPermissions = oldPermissions.filter((p) => !newPermissions.includes(p));
        for (const permission of removedPermissions) {
            const checkBoxes = this.renderRoot.querySelectorAll(
                `ptc-checkbox-button[name="permissions"][value="${permission}"]`
            ) as NodeListOf<CheckboxButton>;
            [...checkBoxes].forEach((el) => (el.checked = false));
        }
    }

    private async _roleChanged(e: Event) {
        const input = e.target as HTMLSelectElement;
        const role = Number(input.value) as Role;

        if (
            role === Role.Owner &&
            !(await confirm(
                "Wollen Sie diesen Mitarbeiter wirklich zum Besitzer dieses Unternehmens machen? Der aktuelle Besitzer wird dann zum Manager degradiert.",
                "Bestätigen",
                "Abbrechen",
                { title: "Besitzer Wechseln", icon: "icon.role.owner" }
            ))
        ) {
            input.value = this.accessRole?.toString() || "";
            return;
        }

        this.requestUpdate();
        await this.updateComplete;

        for (const permission of PERMISSIONS.children.filter(
            (p: PermissionInfo) => p.requiresRole && p.requiresRole < role
        )) {
            const els = this.renderRoot.querySelectorAll(
                `ptc-checkbox-button[name="permissions"][value^="${permission.key}"]`
            ) as NodeListOf<HTMLInputElement>;
            els.forEach((el) => (el.checked = false));
        }

        this._page = role > Role.Manager ? "staff" : "manage";

        this.requestUpdate();
        this.dispatchEvent(new CustomEvent("change"));
    }

    reset() {
        this._form.reset();
    }

    static styles = [
        shared,
        Checkbox.styles,
        css`
            :host {
                display: block;
                position: relative;
            }
        `,
    ];

    private _selectAll(tab: "staff" | "manage") {
        const container = this.renderRoot.querySelector(`#${tab}-permissions`);
        if (!container) return;
        container
            .querySelectorAll<CheckboxButton>('ptc-checkbox-button[name="permissions"]:not([disabled])')
            .forEach((el) => (el.checked = true));
        this.requestUpdate();
        this.dispatchEvent(new CustomEvent("change"));
    }

    private _selectNone(tab: "staff" | "manage") {
        const container = this.renderRoot.querySelector(`#${tab}-permissions`);
        if (!container) return;
        container
            .querySelectorAll<CheckboxButton>('ptc-checkbox-button[name="permissions"]:not([disabled])')
            .forEach((el) => (el.checked = false));
        this.requestUpdate();
        this.dispatchEvent(new CustomEvent("change"));
    }

    private _renderPermissionHeader(tab: "staff" | "manage") {
        return html`
            <div class="small half-padded margined semibold horizontal center-aligning layout">
                <ptc-ui-icon name="icon.object.task"></ptc-ui-icon> Berechtigungen
                <div class="stretch"></div>
                <div ?hidden=${this.readonly}>
                    <button type="button" class="transparent slim" @click=${() => this._selectNone(tab)}>
                        <ptc-ui-icon name="icon.toggle.select-none"></ptc-ui-icon> Keine
                    </button>
                    <button type="button" class="transparent slim" @click=${() => this._selectAll(tab)}>
                        <ptc-ui-icon name="icon.toggle.select-all"></ptc-ui-icon> Alle
                    </button>
                </div>
            </div>
        `;
    }

    private _renderInfoIcon(description: string): TemplateResult {
        return html`<ptc-ui-icon
            name="icon.status.info"
            ${popover(html` <div class="small" style="max-width: 20em;">${description}</div> `, {
                trigger: "hover",
                class: "non-interactive",
                preferAlignment: ["right"],
                style: "z-index: 101;",
            })}
        ></ptc-ui-icon>`;
    }

    private _renderPermission({
        key,
        requires,
        title,
        description,
        children,
        color,
        feature,
        featureFlag,
        grid,
        readonly,
        hide,
        icon,
    }: PermissionInfo): TemplateResult {
        const perms = this.data.permissions;
        const features = (app.company && app.company.features) || [];

        // Build label components separately for clarity
        const iconElement = icon ? html`<ptc-ui-icon name="${icon}"></ptc-ui-icon> ` : "";
        const descriptionIcon = description ? this._renderInfoIcon(description) : "";

        const label = html`${iconElement}${title}${descriptionIcon}`;

        return html`
            <div
                class="${!hide ? "check-group" : ""}"
                ?hidden=${(feature && !features.includes(feature)) ||
                (featureFlag && !this._enabledFeatures?.[featureFlag])}
                ?disabled=${readonly || (requires && requires.some((key) => !perms.includes(key as Permission)))}
                style="--color-highlight: ${colors[color!] || color || "inherit"}; position: relative;"
            >
                ${key
                    ? html`
                          <ptc-checkbox-button
                              data-testid=${`permissions-button-${key}`}
                              .testId=${`permissions-checkbox-${key}`}
                              buttonClass="transparent slim"
                              name="permissions"
                              .value=${key}
                              .label=${label}
                              @input=${this._permissionChanged}
                              data-permission=${key}
                              ?hidden=${hide}
                              ?disabled=${this.readonly ||
                              !canGrantPermission(key as Permission, this.grantingProfile, app.account?.admin)}
                          ></ptc-checkbox-button>
                      `
                    : html` <div class="title">${label}</div> `}
                ${children && children.length
                    ? html`
                          <div
                              class="${grid ? "grid" : ""} children"
                              ?hidden=${!!key && !perms.includes(key as Permission)}
                              style="margin-top: 0"
                          >
                              ${children.map((child) => this._renderPermission(child))}
                          </div>
                      `
                    : ""}
            </div>
        `;
    }

    render() {
        const role = this.data.role;
        const iconStyle = "margin: 0 -0.4em 0 0.4em";

        return html`
            <form id="permissionsForm">
                <div class="fullbleed vertical layout">
                    <div class="padded horizontal spacing layout border-bottom">
                        <div
                            class="small box bold select-wrapper ${role === Role.Owner
                                ? "purple"
                                : role === Role.Manager
                                  ? "orange"
                                  : "grey"}"
                        >
                            ${role === Role.Owner
                                ? html`<ptc-ui-icon name="icon.role.owner" style=${iconStyle}></ptc-ui-icon>`
                                : role === Role.Manager
                                  ? html`<ptc-ui-icon name="icon.role.manager" style=${iconStyle}></ptc-ui-icon>`
                                  : html`<ptc-ui-icon name="icon.role.staff" style=${iconStyle}></ptc-ui-icon>`}
                            <select
                                name="role"
                                class="slim plain transparent"
                                @change=${(e: Event) => this._roleChanged(e)}
                                ?disabled=${role > Role.Owner && this.readonly}
                            >
                                <option .value=${Role.Worker.toString()}>Mitarbeiter</option>
                                <option .value=${Role.Manager.toString()}>Manager</option>
                                <option
                                    .value=${Role.Owner.toString()}
                                    ?disabled=${(app.profile?.role !== Role.Owner && !app.account?.admin) || this.isNew}
                                >
                                    Besitzer
                                </option>
                            </select>
                            <ptc-ui-icon name="icon.navigation.dropdown"></ptc-ui-icon>
                        </div>

                        <div class="stretch"></div>

                        <button class="slim transparent" type="button" ?hidden=${this.readonly || this.isNew}>
                            <ptc-ui-icon name="icon.action.show-more"></ptc-ui-icon>
                        </button>

                        <ptc-popover>
                            <button
                                type="button"
                                class="transparent"
                                ?disabled=${this.readonly || this.isNew}
                                @click=${() => this.dispatchEvent(new CustomEvent("revoke-access"))}
                            >
                                <ptc-ui-icon name="icon.status.blocked"></ptc-ui-icon> Zugang Entziehen
                            </button>
                        </ptc-popover>

                        <div class="horizontal tabs">
                            <button
                                type="button"
                                ?active=${this._page === "staff"}
                                @click=${() => (this._page = "staff")}
                            >
                                <ptc-ui-icon
                                    name=${activeIcon("icon.object.mobile-app", this._page === "staff")}
                                ></ptc-ui-icon>
                                Mitarbeiterzugang
                            </button>
                            <button
                                type="button"
                                ?active=${this._page === "manage"}
                                @click=${() => (this._page = "manage")}
                                ?disabled=${role > Role.Manager}
                            >
                                <ptc-ui-icon
                                    name=${activeIcon("icon.role.manager", this._page === "manage")}
                                ></ptc-ui-icon>
                                Verwaltung
                            </button>
                        </div>
                    </div>

                    <div class="small stretch relative">
                        <ptc-scroller id="staff-permissions" class="fullbleed" ?hidden=${this._page !== "staff"}>
                            ${this._renderPermissionHeader("staff")}
                            ${PERMISSIONS.children
                                .filter(
                                    (scope: PermissionInfo) => !scope.requiresRole || scope.requiresRole < Role.Manager
                                )
                                .map((scope) => this._renderPermission(scope))}
                        </ptc-scroller>

                        <ptc-scroller id="manage-permissions" class="fullbleed" ?hidden=${this._page !== "manage"}>
                            <div class="padded border-bottom">
                                <div class="small half-padded semibold">
                                    <ptc-ui-icon name="icon.object.responsibility"></ptc-ui-icon> Zuständigkeitsbereiche
                                </div>
                                <ptc-entity-filters
                                    name="filters"
                                    @change=${() => this.dispatchEvent(new CustomEvent("change"))}
                                    ?disabled=${role <= Role.Owner || this.readonly}
                                    hideFilterIcon
                                    hideEmployeeCount
                                    .filterTypes=${["venue", "department", "position"] as const}
                                ></ptc-entity-filters>
                            </div>
                            ${this._renderPermissionHeader("manage")}
                            ${PERMISSIONS.children
                                .filter(
                                    (scope: PermissionInfo) => scope.requiresRole && scope.requiresRole >= Role.Manager
                                )
                                .map((perm) => this._renderPermission(perm))}
                        </ptc-scroller>
                    </div>
                </div>
            </form>
        `;
    }
}
